import { useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import {
  ATTEMPT_ITEM_LABEL,
  ATTEMPT_RESULT_LABEL,
  type AttemptItems,
  type AttemptRecord,
  type AttemptResult,
} from '@/lib/progress/history';

interface AttemptHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  lessonId: number;
  attempts: AttemptRecord[];
  onClear: () => void;
}

const DATE_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const RESULT_STYLE: Record<AttemptResult, string> = {
  incomplete: 'bg-secondary-100 text-secondary-800',
  completed: 'bg-primary-100 text-primary-800',
  clean: 'bg-accent-50 text-accent-800',
  mastered: 'bg-accent-100 text-accent-800',
};

const RESULT_ICON: Record<AttemptResult, string> = {
  incomplete: 'ri-pause-circle-line',
  completed: 'ri-check-line',
  clean: 'ri-check-double-line',
  mastered: 'ri-medal-line',
};

const ITEM_KEYS: (keyof AttemptItems)[] = ['note', 'attack', 'duration', 'rest', 'tie'];

function ItemRow({ items }: { items?: AttemptItems }) {
  if (!items) return null;
  const present = ITEM_KEYS.filter((key) => items[key]);
  if (present.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
      {present.map((key) => {
        const tally = items[key];
        if (!tally) return null;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-1.5 rounded-md bg-background-100 px-2 py-1 text-[11px] font-label font-semibold text-foreground-700"
          >
            {ATTEMPT_ITEM_LABEL[key]}
            <span className="inline-flex items-center gap-1 text-accent-700">
              <i className="ri-check-line w-3 h-3 flex items-center justify-center" />
              {tally.correct}
            </span>
            <span className="inline-flex items-center gap-1 text-secondary-700">
              <i className="ri-close-line w-3 h-3 flex items-center justify-center" />
              {tally.wrong}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Painel "Meu histórico": revê, para esta aula (ou todas), cada tentativa e
 * cada exercício com data, resultado e contagem por item. Os dados vêm do
 * progresso local e continuam disponíveis depois de recarregar a página.
 */
export default function AttemptHistoryPanel({
  open,
  onClose,
  lessonId,
  attempts,
  onClear,
}: AttemptHistoryPanelProps) {
  const [onlyThisLesson, setOnlyThisLesson] = useState(true);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const filtered = useMemo(
    () => (onlyThisLesson ? attempts.filter((item) => item.lessonId === lessonId) : attempts),
    [attempts, lessonId, onlyThisLesson],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground-950/40 p-0 sm:items-center sm:p-4">
      <div
        className="flex max-h-[88vh] w-full flex-col rounded-t-lg border border-background-200 bg-background-50 sm:max-w-3xl sm:rounded-lg"
        role="dialog"
        aria-modal="true"
        aria-label="Meu histórico"
      >
        <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3 md:px-6">
          <div className="flex items-center gap-2">
            <i className="ri-history-line w-5 h-5 flex items-center justify-center text-primary-600" />
            <span className="font-heading text-base font-bold text-foreground-950">Meu histórico</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-3 md:px-6">
          <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setOnlyThisLesson(true)}
              className={`rounded-full px-3 py-1 text-xs font-label font-semibold transition-colors cursor-pointer ${
                onlyThisLesson ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
              }`}
            >
              Só esta aula
            </button>
            <button
              type="button"
              onClick={() => setOnlyThisLesson(false)}
              className={`rounded-full px-3 py-1 text-xs font-label font-semibold transition-colors cursor-pointer ${
                !onlyThisLesson ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
              }`}
            >
              Todas as aulas
            </button>
          </div>
          <span className="text-xs text-foreground-500">
            {filtered.length} registro{filtered.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 md:px-6">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-inbox-line text-xl" />
              </span>
              <p className="mt-3 text-sm text-foreground-600">
                Ainda não há registros {onlyThisLesson ? 'desta aula' : ''}. Pratique um pouco e o
                histórico aparece aqui.
              </p>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {filtered.map((record) => (
                <li
                  key={record.id}
                  className="rounded-lg border border-background-200 bg-background-100/50 p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-label font-semibold ${RESULT_STYLE[record.result]}`}
                      >
                        <i className={`${RESULT_ICON[record.result]} w-3.5 h-3.5 flex items-center justify-center`} />
                        {ATTEMPT_RESULT_LABEL[record.result]}
                      </span>
                      <span className="font-label text-xs font-semibold text-foreground-700">
                        Aula {record.lessonId}
                        {record.scope === 'exercise' && record.exerciseIndex !== undefined
                          ? ` · Exercício ${record.exerciseIndex + 1}`
                          : ''}
                        {record.timeSignature ? ` · ${record.timeSignature}` : ''}
                      </span>
                    </div>
                    <span className="text-[11px] text-foreground-500">
                      {DATE_FORMAT.format(new Date(record.at))}
                      {typeof record.accuracy === 'number' ? ` · ${record.accuracy}%` : ''}
                    </span>
                  </div>
                  <ItemRow items={record.items} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-background-200 px-4 py-3 md:px-6">
          <span className="text-[11px] text-foreground-500">
            Guardamos os registros mais recentes. O desbloqueio das aulas não é afetado.
          </span>
          {confirmingClear ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-label font-semibold text-secondary-800">Apagar tudo?</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClear();
                  setConfirmingClear(false);
                }}
              >
                Sim, apagar
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingClear(false)}>
                Cancelar
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              icon="ri-delete-bin-line"
              disabled={attempts.length === 0}
              onClick={() => setConfirmingClear(true)}
            >
              Limpar histórico
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}